import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import { getMeetingReservationDetail, TReservation } from "apis/meetings/meeting-reservation";
import { useRecoilValue } from "recoil";
import { member } from "recoil/recoilState";
import ReservationInfoModal from "./reservation-info-modal";
import { useState } from "react";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import { getModalPosition, handleOpenInfo, isReservationEditable } from "./services";
import { UserRole } from "utils/role";

export type TColor = "pink" | "yellow" | "orange" | "cyan" | "lime" | "skyblue" | "purple";
type ReservationItemProps = {
  color?: TColor;
  reservation?: TReservation;
  height?: number;
  top?: number;
  styles?: React.CSSProperties;
  meetingRoom?: TMeetingRoom;
  onHandleEdit?: (reservation?: TReservation, meetingRoom?: TMeetingRoom, isMyReservation?: boolean) => void;
  onUpdate?: () => void;
};

export const getBackgroundColor = (color: TColor) => {
  switch (color) {
    case "pink":
      return "var(--alias-color-accent-light-pink)";
    case "yellow":
      return "rgba(255, 248, 229, 0.6)";
    case "orange":
      return "var(--alias-color-accent-light-orange)";
    case "cyan":
      return "var(--alias-color-accent-light-cyan)";
    case "lime":
      return "var(--alias-color-accent-light-lime)";
    case "skyblue":
      return "var(--alias-color-accent-light-skyblue)";
    case "purple":
      return "var(--alias-color-accent-light-purple)";
  }
};

export const getBorderColor = (color: TColor) => {
  switch (color) {
    case "pink":
      return "var(--alias-color-accent-normal-pink)";
    case "yellow":
      return "rgba(255, 214, 102, 1)";
    case "orange":
      return "var(--alias-color-accent-normal-orange)";
    case "cyan":
      return "var(--alias-color-accent-normal-cyan)";
    case "lime":
      return "var(--alias-color-accent-normal-lime)";
    case "skyblue":
      return "var(--alias-color-accent-normal-skyblue)";
    case "purple":
      return "var(--alias-color-accent-normal-purple)";
  }
};
const ReservationItem = (props: ReservationItemProps) => {
  const [openInfo, setOpenInfo] = useState(false);
  const memberInfo = useRecoilValue(member);
  const { color = "pink", height = 56, top, styles, reservation, meetingRoom, onUpdate } = props;
  const isMyReservation = reservation?.reservationCreator?.member?.id === memberInfo?.id;
  const [widgetPosition, setWidgetPosition] = useState<{ top?: number; left?: number; right?: number }>();
  const startTime = new Date(reservation?.startTime ?? "");
  const endTime = new Date(reservation?.endTime ?? "");
  const [detailRsvInfo, setDetailRsvInfo] = useState<TReservation>();

  const isNotReadPermission =
    memberInfo?.email.includes("@tosspartners.com") || memberInfo?.email.includes("@tossinvestpartners.com");

  return (
    <>
      <Container
        height={height}
        color={color}
        style={styles}
        top={top}
        onClick={async (e) => {
          if (isNotReadPermission && !reservation?.isMyMeeting) return;
          const rect = e.currentTarget.getBoundingClientRect();

          //* 예약 상세 정보 모달 위치 설정
          const position = getModalPosition(rect.top, rect.right, rect.bottom, rect.left);
          setWidgetPosition(position);

          //* 예약 상세 정보 조회 & 상세 모달 오픈
          if (reservation && memberInfo) {
            const res = await handleOpenInfo(
              reservation.googleEventId ?? "",
              reservation.id,
              meetingRoom?.id ?? "",
              isMyReservation,
              reservation.isPrivate ?? false,
              memberInfo?.role,
              reservation.isMyMeeting
            );

            if (res) {
              setDetailRsvInfo(res);
              setOpenInfo(true);
            }
          }
        }}
      >
        <TitleContainer>
          <div
            style={{
              display: "flex",
              gap: "4px",
              alignItems: "center",
              width: "100%",
            }}
          >
            {reservation?.isMyMeeting && <Eclipse color={color} />}
            <UI.Typography
              type="caption01"
              fontStyle="bold"
              color="var(--alias-color-label-normal)"
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {isNotReadPermission && !reservation?.isMyMeeting
                ? ""
                : reservation?.isPrivate ?? false
                  ? "비공개 회의"
                  : reservation?.title ?? "-"}
            </UI.Typography>
          </div>
        </TitleContainer>
        {height > 28 && (
          <UI.Typography
            type="caption02"
            fontStyle="bold"
            color={getBorderColor(color)}
            style={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "100%",
              whiteSpace: "nowrap",
            }}
          >
            {startTime.getHours().toString().padStart(2, "0")}:{startTime.getMinutes().toString().padStart(2, "0")} ~
            {endTime.getHours().toString().padStart(2, "0")}:{endTime.getMinutes().toString().padStart(2, "0")}
          </UI.Typography>
        )}
        <Interaction />
      </Container>
      <ReservationInfoModal
        isMyReservation={reservation?.isMyMeeting}
        top={widgetPosition?.top}
        left={widgetPosition?.left}
        onHandleEdit={props.onHandleEdit}
        meetingRoom={meetingRoom}
        reservationInfo={detailRsvInfo}
        open={openInfo}
        right={widgetPosition?.right}
        setOpen={setOpenInfo}
        color={getBorderColor(color)}
        canEdit={isReservationEditable(
          isMyReservation,
          reservation?.isMyMeeting ?? false,
          memberInfo?.role ?? UserRole.UnRole,
          reservation?.guestsCanModify ?? false,
          reservation?.rsvStatus ?? ""
        )}
        onUpdate={onUpdate}
      />
    </>
  );
};

export default ReservationItem;

const Container = styled.div<{ height: number; color: TColor; top?: number }>`
  position: absolute;
  background-color: ${(props) => getBackgroundColor(props.color)};
  border: 2px solid ${(props) => getBorderColor(props.color)};
  border-radius: 8px;

  display: flex;
  top: ${(props) => props.top || 0}px;
  box-sizing: border-box;
  width: 100%;
  height: ${(props) => props.height}px;
  padding: ${(props) => (props.height > 28 ? "12px" : "4px 12px")};
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Eclipse = styled.div<{ color: TColor }>`
  background-color: ${(props) => getBorderColor(props.color)};
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const Interaction = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;

  background-color: var(--alias-color-label-normal);
  opacity: 0;

  &:hover {
    opacity: 0.06;
  }
  &:active {
    opacity: 0.09;
  }
`;
